// eslint-disable-next-line no-restricted-imports
import { FC, useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect, useDispatch } from 'react-redux'

import clsx from 'clsx'
import { createStructuredSelector } from 'reselect'

import { Box, Button, Card, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { Theme } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles'
import withStyles from '@mui/styles/withStyles'
import { DateRangePicker } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import BlackTooltip from 'components/common/tooltips'
import { Context } from 'components/core/Acquiring/AcquiringCharges/components/AcquiringChargesContext'
import { Spinner } from 'components/core/Progress/Progress-component'
import { useDateRangePicker } from 'hooks/useDateRangePicker'
import {
    finishAcquiringChargesDownload,
    startAcquiringChargesDownload,
    startGetCardAcquiringCharges,
} from 'redux/acquiring/acquiring-actions'
import { selectAcquiringChargesDownload, selectAcquiringLoader } from 'redux/acquiring/acquiring-select'
import { IMAGES_PATH, themeStyles } from 'themes'
import combineStyles from 'utils/combineStyles'

const pdfOval = IMAGES_PATH('/downloadOval.svg')
const csvOval = IMAGES_PATH('/upload-documents.svg')

const styles = makeStyles<Theme>(theme => ({
    box: {
        [theme.breakpoints.up('md')]: {
            '& > div:first-child': {
                width: '50% !important',
            },
        },
        [theme.breakpoints.down('lg')]: {
            flexDirection: 'column',

            '& .dateContainer > div': {
                flexDirection: 'column',
            },
        },
    },
    dateRangeField: {
        width: '100%',
        '& + div': {
            marginLeft: 20,

            [theme.breakpoints.down('lg')]: {
                margin: '20px 0 0',
            },
        },
    },
    downloadBtnWrap: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        minHeight: 38,

        '& > div, & > img': {
            width: 38,
            height: 38,
            marginRight: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },

        [theme.breakpoints.down('lg')]: {
            justifyContent: 'flex-start',
            marginBottom: 20,
            '& > div:last-child, & > img:last-child': {
                marginRight: 0,
            },
        },
    },
    downloadBtn: {
        cursor: 'pointer',
    },
    selectInputs: {
        minWidth: 210,
        flex: 1,
        margin: '0',
        [theme.breakpoints.down('md')]: {
            margin: '0 0 20px 0 !important',
        },
        [theme.breakpoints.down('lg')]: {
            width: '100%',

            // '&:nth-child(2)': {
            //     marginLeft: '20px',
            // },
            '&:last-child': {
                marginRight: 0,
            },

            '& > div': {
                marginRight: 0,
            },
        },
    },
    selectStatus: {
        [theme.breakpoints.down('md')]: {},
        [theme.breakpoints.down('lg')]: {
            marginBottom: '20px !important',
            marginRight: '0 !important',
        },
    },
    filterButton: {
        [theme.breakpoints.down('lg')]: {
            width: '100% !important',
        },
        [theme.breakpoints.down('md')]: {
            marginTop: '0 !important',
        },
    },
}))

interface IProps {
    classes: any
    loader: any
    download: any
    startAcquiringChargesDownload: any
    finishAcquiringChargesDownload: any
}

const AcquiringDashboardFilter: FC<IProps> = ({
    classes,
    loader,
    download,
    startAcquiringChargesDownload,
    finishAcquiringChargesDownload,
}) => {
    const style = styles()
    const dispatch = useDispatch()
    const { t } = useTranslation(['override', 'translations', 'keys'])
    const { chargesInputs, setChargesInputs } = useContext(Context)
    const { from, to, status } = chargesInputs
    const { dateRangeValue, handleDateRangeValue, dateRangeStart, dateRangeEnd } = useDateRangePicker('YYYY-MM-DD')
    const [type, setType] = useState('')

    useEffect(() => {
        setChargesInputs(prevState => ({ ...prevState, from: dateRangeStart || '', to: dateRangeEnd || '' }))
    }, [dateRangeStart, dateRangeEnd])

    const handleStatusChange = (value?: string | null) => {
        setChargesInputs({ ...chargesInputs, status: value })
    }

    const handleFilter = () => {
        dispatch(startGetCardAcquiringCharges({ ...chargesInputs, from, to, status }))
    }
    const Download = useCallback(
        file => {
            const element = document.createElement('a')

            element.href = URL.createObjectURL(file)
            element.download = `Charges ${from} -  q${to}.${type}`
            document.body.appendChild(element)
            element.click()
            finishAcquiringChargesDownload()
        },
        [finishAcquiringChargesDownload, type]
    )

    useEffect(() => {
        if (download) {
            const file = new Blob([download], { type: type === 'pdf' ? 'application/pdf' : 'text/csv' })

            download && Download(file)
        }
    }, [download, Download, type])

    const statuses: ICardPaymentStatus[] = ['Initiated', 'Paid', 'Refunded', 'ChargeBacked', 'Declined', 'Abandoned']

    const handleDownload = chosenType => {
        setType(chosenType)
        startAcquiringChargesDownload({ from, to, status, type: chosenType })
    }

    return (
        <Card classes={{ root: classes.card }}>
            <Box display="flex" alignItems="center" className={clsx(classes.box, style.box)}>
                <div className="dateContainer">
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        localeText={{ start: t('TransactionsFilter.From'), end: t('TransactionsFilter.To') }}
                    >
                        <DateRangePicker
                            value={dateRangeValue}
                            showToolbar={false}
                            onChange={handleDateRangeValue}
                            renderInput={(startProps, endProps) => {
                                startProps.inputProps = {
                                    ...startProps.inputProps,
                                    'data-sta-id': 'PaymentGateway-charges-dateRangePicker-startDate',
                                }
                                endProps.inputProps = {
                                    ...endProps.inputProps,
                                    'data-sta-id': 'PaymentGateway-charges-dateRangePicker-endDate',
                                }

                                return (
                                    <>
                                        <TextField className={style.dateRangeField} {...startProps} inputProps={startProps.inputProps} />
                                        <TextField className={style.dateRangeField} {...endProps} inputProps={endProps.inputProps} />
                                    </>
                                )
                            }}
                        />
                    </LocalizationProvider>
                </div>

                <FormControl
                    variant="outlined"
                    margin="normal"
                    className={clsx(classes.selectInputs, style.selectInputs, style.selectStatus)}
                >
                    <InputLabel id="acqquiring-charges-status-input">{t('TransactionsFilter.Status')}</InputLabel>
                    <Select
                        value={status || ''}
                        name="status"
                        onChange={e => {
                            handleStatusChange(e.target.value as string)
                        }}
                        label="Status"
                    >
                        <MenuItem value="">{t('forms:Company.Select')}</MenuItem>
                        {statuses.map((val, i) => (
                            <MenuItem key={i} value={val}>
                                {t(`keys:chargeStatus.${val}`)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <div className={style.downloadBtnWrap}>
                    <div>
                        {loader?.ACQUIRING_CHARGES_DOWNLOAD ? (
                            <Spinner />
                        ) : (
                            <BlackTooltip data-clipboardtarget title={`${t('Acquiring.Download')} PDF`} placement="top" arrow={true}>
                                <img
                                    src={pdfOval}
                                    alt={`${t('Acquiring.Download')} PDF`}
                                    onClick={() => handleDownload('pdf')}
                                    className={style.downloadBtn}
                                />
                            </BlackTooltip>
                        )}
                    </div>

                    <div>
                        {loader?.ACQUIRING_CHARGES_DOWNLOAD ? (
                            <Spinner />
                        ) : (
                            <BlackTooltip data-clipboardtarget title={`${t('Acquiring.Download')} CSV`} placement="top" arrow={true}>
                                <img
                                    src={csvOval}
                                    alt={`${t('Acquiring.Download')} CSV`}
                                    onClick={() => handleDownload('csv')}
                                    className={style.downloadBtn}
                                />
                            </BlackTooltip>
                        )}
                    </div>
                </div>

                <Button
                    onClick={handleFilter}
                    className={clsx(classes.filterButton, style.filterButton)}
                    variant="contained"
                    color="primary"
                >
                    {t('Acquiring.Filter')}
                </Button>
            </Box>
        </Card>
    )
}

const mapDispatchToProps = dispatch => ({
    startAcquiringChargesDownload: value => dispatch(startAcquiringChargesDownload(value)),
    finishAcquiringChargesDownload: value => dispatch(finishAcquiringChargesDownload(value)),
})
const mapStateToProps = createStructuredSelector({
    download: selectAcquiringChargesDownload,
    loader: selectAcquiringLoader,
})

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(combineStyles(themeStyles.useAcquiringStyles, themeStyles.useTableStyles))(AcquiringDashboardFilter))
